import { useContext } from 'react';
import './filterChips.scss';
import { DesignSystemContext } from '../../context/DesignSystemContext';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { filterChipsI18n } from './i18n';

/**
 * Reusable bar to display active filters as removable chips.
 *
 * Props:
 * - filters: Array<{
 *     id: string;            // unique id for the filter (e.g. field name)
 *     label: string;         // human label (e.g. "Usuarios", "Tribunales")
 *     value: string;         // rendered value (e.g. "User_1, User_2")
 *   }>
 * - onRemove: (id: string) => void        // remove a single filter
 * - onClearAll?: () => void               // optional: clear all filters at once
 */
const FilterChips = ({ filters = [], onRemove, onClearAll }) => {
  const designSystem = useContext(DesignSystemContext);
  const IconButton = designSystem?.IconButton;
  const Button = designSystem?.Button;

  const { t } = useScopedTranslation(filterChipsI18n);

  if (!filters || filters.length === 0) return null;

  return (
    <div className='filter-chips-bar d-flex justify-content-between align-items-center mb-3'>
      <div className='d-flex flex-wrap gap-2'>
        {filters.map((chip) => (
          <div
            key={chip.id}
            className='filter-chip d-flex align-items-center px-2 py-1'
          >
            <span className='mini fw-normal me-1'>
              <strong>{chip.label}:</strong> {chip.value}
            </span>
            {IconButton && (
              <IconButton
                ariaLabel={`${t('removeAriaLabel')} ${chip.label}`}
                icon='close'
                size='sm'
                onClick={() => onRemove && onRemove(chip.id)}
              />
            )}
          </div>
        ))}
      </div>

      {onClearAll && Button && (
        <Button
          label={t('clearAll')}
          variant='naked'
          size='sm'
          onClick={onClearAll}
        />
      )}
    </div>
  );
};

export default FilterChips;
